/* eslint-disable complexity */
import styled from '@emotion/styled';
import {
  DeleteOutline,
  EditOutlined,
  FormatTextdirectionRToLOutlined,
  MoveDownOutlined,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useDeleteBranch from 'api/productDetail/useDeleteBranch/useDeleteBranch';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { useEffect, useMemo, useState } from 'react';

import AccordionCodingList from '../AccordionCodingList/AccordionCodingList';
import AccordionPackaging from '../AccordionPackaging/AccordionPackaging';
import BranchDisplacementModal from '../BranchDisplacementModal/BranchDisplacementModal';
import BranchPathfixModal from './BranchPathfixModal/BranchPathfixModal';

const ProductAccordion = ({
  detail,
  getProductDetail,
  refreshProductBranchList,
  expanded,
  setExpand,
  keyIndex = 0,
  setBranchGallery,
  setSystemEdit,
  setManualEdit,
  setEditMapping,
  setCreateCoddingGuid,
  refetchDetails,
  handleEditDefaultBranch,
}) => {
  const { codding, display, coddingList, packagingList, guid } = detail;

  const [orderedCoddingList, setOrderedCoddingList] = useState();
  const [orderedPackegingList, setOrderedPackegingList] = useState();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditBranchModalOpen, setIsEditBranchModalOpen] = useState(false);
  const [isPathfixModalOpen, setIsPathfixModalOpen] = useState(false);
  const [isDisplacementModalOpen, setIsDisplacementModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [deleteBranch, deleteLoading] = useDeleteBranch();

  const { can } = usePermissions();

  const openMenu = Boolean(anchorEl);

  const handleClickMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const deleteHandle = () => {
    deleteBranch({ branchGuid: guid }, () => refetchDetails());
  };

  const memoCoddingList = useMemo(() => {
    return coddingList;
  }, [coddingList]);

  useEffect(() => {
    setOrderedCoddingList(
      coddingList.map(code => ({
        codding: code.codding,
        display: code.display,
        profileName: code.profileName,
        id: code.guid,
        isReadOnly: code.isReadOnly,
        isDefault: code.isDefault,
        isTitleRequired: code.isTitleRequired,
        type: code.type,
        logic: code.logic,
      })),
    );
  }, [memoCoddingList]);

  useEffect(() => {
    setOrderedPackegingList(
      packagingList.map(code => ({
        title: code.display,
        id: code.guid,
        count: code.count,
      })),
    );
  }, []);

  const handleEditBranch = () => {
    const defaultCode = coddingList?.find(code => code.isDefault === true);

    handleEditDefaultBranch(defaultCode);
  };

  const expand = expanded === guid;

  const productBranchPermissions =
    permissions.general.categories.globalSettings.productManagement.productList
      .detail.branch;

  const menuItems = useMemo(
    () => [
      {
        label: 'ویرایش',
        permission: productBranchPermissions.pathfix,
        icon: EditOutlined,
        color: 'warning',
        onClick: () => {
          handleCloseMenu();
          handleEditBranch();
        },
      },
      {
        label: 'پسوند',
        permission: productBranchPermissions.pathfix,
        icon: FormatTextdirectionRToLOutlined,
        color: 'success',
        onClick: () => {
          handleCloseMenu();
          setIsPathfixModalOpen(true);
        },
      },
      {
        label: 'جابجایی',
        permission: productBranchPermissions.displacement,
        icon: MoveDownOutlined,
        color: 'secondary',
        onClick: () => {
          handleCloseMenu();
          setIsDisplacementModalOpen(true);
        },
      },
      {
        label: 'حذف',
        permission: productBranchPermissions.delete,
        icon: DeleteOutline,
        color: 'error',
        onClick: () => {
          handleCloseMenu();
          setIsDeleteModalOpen(true);
        },
      },
    ],
    [],
  );

  const visibleMenuItems = menuItems.filter(item => can(item.permission));

  const canViewGallery = can(productBranchPermissions.gallery);

  const canCreatePartNumber = can(productBranchPermissions.createPartNumber);

  const canViewMenu = visibleMenuItems.length > 0;

  return (
    <Paper
      square
      elevation={1}
      sx={{
        p: 2,
        pb: expand ? 5 : 2,
        m: expand ? (keyIndex === 0 ? '0 0 20px 0' : '20px 0') : '0',
        transition: '.25s',
        borderTop: '1px solid rgba(100,100,100,.1)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
          gap: '15px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: {
              xs: 'auto',
              lg: '450px',
              xl: '550px',
            },
          }}
        >
          <Box sx={{ width: '50px' }}>
            <IconButton onClick={setExpand}>
              <ExpandMoreIcon
                style={{
                  transform: expand ? 'rotateZ(0)' : 'rotateZ(180deg)',
                }}
              />
            </IconButton>
          </Box>

          <Typography>{display}</Typography>
        </Box>

        <Typography>{codding}</Typography>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingLeft: '16px',
            gap: 1,
          }}
        >
          {canViewGallery ? (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => {
                setBranchGallery();
              }}
            >
              گالری
            </Button>
          ) : null}

          {canViewMenu ? (
            <div>
              <IconButton
                id="product-accordion-button"
                aria-controls={openMenu ? 'product-accordion-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openMenu ? 'true' : undefined}
                onClick={handleClickMenu}
              >
                <MenuIcon />
              </IconButton>
            </div>
          ) : null}
        </Box>

        {canViewMenu ? (
          <Menu
            id="product-accordion-menu"
            aria-labelledby="product-accordion-button"
            anchorEl={anchorEl}
            open={openMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={handleCloseMenu}
          >
            {visibleMenuItems.map(item => {
              const Icon = item.icon;

              return (
                <MenuItem key={item.permission} onClick={item.onClick}>
                  <Action>
                    <Typography fontSize={14}>{item.label}</Typography>

                    <Icon fontSize="small" color={item.color} />
                  </Action>
                </MenuItem>
              );
            })}
          </Menu>
        ) : null}
      </Box>

      <Collapse in={expand} timeout="auto" unmountOnExit>
        <div>
          <CollapseInnerContainer>
            <AccordionCodingList
              setCreateCoddingGuid={setCreateCoddingGuid}
              coddingList={orderedCoddingList}
              getProductDetail={getProductDetail}
              refreshProductBranchList={refreshProductBranchList}
              setSystemEdit={setSystemEdit}
              setManualEdit={setManualEdit}
              setEditMapping={setEditMapping}
            />
          </CollapseInnerContainer>

          <CollapseInnerContainer>
            <AccordionPackaging
              packagingList={orderedPackegingList}
              refetchPackagingList={refetchDetails}
              getProductDetail={getProductDetail}
              branchGuid={guid}
            />
          </CollapseInnerContainer>
        </div>

        {canCreatePartNumber ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button variant="contained" color="primary">
              افزودن پارت نامبر
            </Button>
          </Box>
        ) : null}
      </Collapse>

      {isDeleteModalOpen ? (
        <DeleteModal
          open={isDeleteModalOpen}
          title={display}
          locationId={guid}
          deleteLoading={deleteLoading}
          handleClose={closeDeleteModal}
          onDelete={deleteHandle}
        />
      ) : null}

      {isPathfixModalOpen ? (
        <BranchPathfixModal
          open={isPathfixModalOpen}
          handleClose={() => {
            setIsPathfixModalOpen(false);
            refetchDetails();
          }}
          branchGuid={guid}
        />
      ) : null}

      {isDisplacementModalOpen ? (
        <BranchDisplacementModal
          open={isDisplacementModalOpen}
          branchGuid={guid}
          handleClose={() => setIsDisplacementModalOpen(false)}
          refetchDetails={refetchDetails}
        />
      ) : null}
    </Paper>
  );
};

export default ProductAccordion;

const CollapseInnerContainer = styled.div`
  margin-bottom: 15px;
  width: 100%;
  overflow-x: scroll;
`;

const HeaderDesc = styled.p`
  color: #707070;
  font-size: 14px;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
